"use client";

import { useEffect, useRef, useState } from "react";
import { ReactNode } from "react";

function findScrollContainer(el: Element | null): Element | null {
  while (el) {
    const { overflow, overflowY } = getComputedStyle(el);
    if (
      overflow === "auto" ||
      overflow === "scroll" ||
      overflowY === "auto" ||
      overflowY === "scroll"
    ) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

// The shrink is only worth it on pages with real scroll room. A page that is
// barely taller than the viewport would otherwise collapse its header over a
// few pixels of travel, which reads as a glitch.
const COLLAPSE_ENABLE_RATIO = 1.5;
// Collapsing shortens the page, so once collapsed we measure against a lower
// bar — without the hysteresis a borderline page can flip back and forth.
const COLLAPSE_KEEP_RATIO = 1.4;

export function PageHeaderClient({
  children,
  hasBreadcrumbs,
}: {
  children: ReactNode;
  hasBreadcrumbs: boolean;
}) {
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = findScrollContainer(
      headerRef.current?.parentElement ?? null
    );
    if (!container) return;

    let enabled = false;
    const sync = () => {
      const ratio = container.scrollHeight / (container.clientHeight || 1);
      enabled =
        ratio >= (enabled ? COLLAPSE_KEEP_RATIO : COLLAPSE_ENABLE_RATIO);
      const isCollapsed = enabled && container.scrollTop > 0;
      setScrolled(isCollapsed);
      // Published on the scroll container so SIBLINGS of the header can react
      // to its height too — an expanded header takes ~3rem more room, which a
      // sticky full-height sidebar has to subtract from its own cap or it
      // overshoots the viewport (see AiAssistantCore's sidebar variant).
      container.setAttribute(
        "data-page-scrolled",
        isCollapsed ? "true" : "false"
      );
    };

    sync();
    container.addEventListener("scroll", sync, { passive: true });

    // Content height is dynamic (async loads, expanding rows) — re-measure
    // whenever the container or its content resizes.
    const observer = new ResizeObserver(sync);
    observer.observe(container);
    for (const child of Array.from(container.children)) observer.observe(child);

    return () => {
      container.removeEventListener("scroll", sync);
      observer.disconnect();
      container.removeAttribute("data-page-scrolled");
    };
  }, []);

  return (
    <div
      ref={headerRef}
      data-scrolled={scrolled ? "true" : "false"}
      className={`group bg-card sticky top-0 z-30 mb-4 border-b transition-[padding] duration-200 ${
        scrolled ? "py-2" : hasBreadcrumbs ? "pt-4 pb-3" : "pt-6 pb-3"
      }`}
    >
      {children}
    </div>
  );
}
